import React, { useState, useMemo } from 'react';
import {
    View,
    StyleSheet,
    Pressable,
    ActivityIndicator,
    Platform,
    StatusBar,
    Modal,
} from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { Ionicons } from '@expo/vector-icons';
import { WebView } from 'react-native-webview';
import { useTheme } from '../contexts/ThemeContext';

interface YouTubeWebViewModalProps {
    visible: boolean;
    videoId: string;
    onClose: () => void;
}

export default function YouTubeWebViewModal({ visible, videoId, onClose }: YouTubeWebViewModalProps) {
    const { colors, themeMode } = useTheme();
    const insets = useSafeAreaInsets();
    const [isLoading, setIsLoading] = useState(true);

    // Build YouTube mobile URL
    const youtubeUrl = useMemo(() => {
        // Use regular YouTube mobile URL - embeds can cause error 153 in WebView
        return `https://m.youtube.com/watch?v=${videoId}`;
    }, [videoId]);

    if (Platform.OS === 'web') {
        return null;
    }

    return (
        <Modal
            visible={visible}
            animationType="slide"
            presentationStyle="fullScreen"
            onRequestClose={onClose}
        >
            <View style={styles.container}>
                <StatusBar barStyle="light-content" />

                {/* WebView takes full screen */}
                <WebView
                    source={{ uri: youtubeUrl }}
                    style={styles.webview}
                    allowsFullscreenVideo
                    allowsInlineMediaPlayback
                    mediaPlaybackRequiresUserAction={false}
                    javaScriptEnabled
                    domStorageEnabled
                    onLoadStart={() => setIsLoading(true)}
                    onLoadEnd={() => setIsLoading(false)}
                />

                {/* Floating close button - positioned below notch using insets */}
                <View
                    style={[
                        styles.closeButtonContainer,
                        { top: insets.top + 8 }
                    ]}
                    pointerEvents="box-none"
                >
                    <Pressable onPress={onClose} style={styles.closeButton}>
                        <Ionicons name="close" size={24} color="#fff" />
                    </Pressable>
                </View>

                {/* Loading overlay */}
                {isLoading && (
                    <View style={styles.loadingOverlay}>
                        <ActivityIndicator size="large" color={colors.primary} />
                    </View>
                )}
            </View>
        </Modal>
    );
}

const styles = StyleSheet.create({
    container: {
        flex: 1,
        backgroundColor: '#000',
    },
    webview: {
        flex: 1,
        backgroundColor: '#000',
    },
    closeButtonContainer: {
        position: 'absolute',
        left: 16,
    },
    closeButton: {
        width: 40,
        height: 40,
        borderRadius: 20,
        backgroundColor: 'rgba(0, 0, 0, 0.7)',
        justifyContent: 'center',
        alignItems: 'center',
    },
    loadingOverlay: {
        ...StyleSheet.absoluteFillObject,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: '#000',
    },
});

